import React, { useEffect, useMemo, useState } from 'react';
import {
  Picture,
  DangerousElement,
  CompleteVehicleEntry,
  FeaturesText,
  urlSeparatorFor,
  useDesktopOrMobileView,
  Features,
} from '@oneaudi/vtp-shared';
import { useI18n } from '@oneaudi/i18n-context';
import Product, { ProductType, ProductVideo } from './Product';
import parse, { DOMNode, domToReact } from 'html-react-parser';
import { getTyreLabelLinks, getValueByKey, isTyre, splitEquipments } from './utils';
import { Headline, Text, List, ListItemCustom } from '@oneaudi/unified-web-components';
import {
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_XL,
  SYS_SPACE_RELATIVE_BP_SM_2_XL,
  SYS_SPACE_RELATIVE_BP_SM_LG,
} from '@oneaudi/unified-web-common';
import {
  HeadlineAndInfoContainer,
  ImageContainer,
  InfoTextContainer,
  InPackageProductsContainer,
  ModalContentContainer,
  HeadlinePanel,
  InfoPanel,
  SupElement,
} from './styles';
import { TyreLabel } from './TyreLabel';
import { EquipmentsPagination } from './EquipmentsPagination';

export interface Props {
  product: ProductType;
  vehicleRaw: CompleteVehicleEntry;
}

const createProductHero = (
  productName: string,
  productImage?: Picture,
  productVideo?: ProductVideo
) => {
  if (productVideo && productVideo.url) {
    return <video src={productVideo.url} poster={productVideo.posterUrl} controls />; // eslint-disable-line jsx-a11y/media-has-caption
  }
  if (productImage && productImage.url) {
    const urlSeparator = productImage ? urlSeparatorFor(productImage.url) : '?';

    return (
      <ImageContainer>
        <picture data-testid="productImage">
          {productImage.url.indexOf('mediaservice') > -1 && (
            <>
              <source
                srcSet={`${productImage.url + urlSeparator}wid=400&mimetype=image/webp 1x, ${
                  productImage.url + urlSeparator
                }wid=600&mimetype=image/webp 2x`}
                media="(max-width: 567px)"
                type="image/webp"
              />
              <source
                srcSet={`${productImage.url + urlSeparator}wid=900&mimetype=image/webp`}
                media="(max-width: 900px)"
                type="image/webp"
              />
              <source
                srcSet={`${productImage.url + urlSeparator}wid=1000&mimetype=image/webp`}
                media="(min-width: 901px)"
                type="image/webp"
              />
            </>
          )}
          <img src={productImage.url} alt={productName} />
        </picture>
      </ImageContainer>
    );
  }
  return <></>;
};

const ModalContent: React.FC<Props> = ({ product, vehicleRaw }) => {
  if (!product || !product.texts) {
    return null;
  }

  const benefitsHeadline = useI18n({
    id: 'nemo.ui.sc.details.mediathek.benefits.headline',
  });
  const packageContentHeadline = useI18n({
    id: 'nemo.ui.sc.details.mediathek.package.headline',
  });

  // eslint-disable-next-line no-nested-ternary
  const productTexts: FeaturesText[] | undefined = product.texts
    ? Array.isArray(product.texts)
      ? product.texts
      : [product.texts]
    : undefined;

  const productImage = getValueByKey<Picture>(product.images, 'small_hd_image');
  const productVideo = getValueByKey<ProductVideo>(product.videos, 'vmrs_video');
  const headline = getValueByKey<FeaturesText>(productTexts, 'ak_headline');
  const remarks = getValueByKey<FeaturesText>(productTexts, 'ak_remarks');
  const description = getValueByKey<FeaturesText>(productTexts, 'ak_text');
  const benefits = getValueByKey<FeaturesText>(productTexts, 'benefits');
  const intro = getValueByKey<FeaturesText>(productTexts, 'intro');

  const features = vehicleRaw?.detail?.features;
  const packageContent = features?.filter((feature) => feature.memberOfPackage === product.family);
  const splittedPackageContent = packageContent && splitEquipments(packageContent);
  const textOptions = {
    // eslint-disable-next-line react/no-unstable-nested-components
    replace: (domNode: DOMNode) => {
      if (!domNode) {
        return null;
      }

      if ('tagName' in domNode && domNode.tagName === 'ul') {
        return <List>{domToReact(domNode.children as DOMNode[], textOptions)}</List>;
      }
      if ('tagName' in domNode && domNode.tagName === 'li') {
        return (
          <ListItemCustom>
            <Text
              variant={{ type: 'copy', order: '2', style: 'normal' }}
              data-testid="customListItem"
            >
              {domToReact(domNode.children as DOMNode[], textOptions)}
            </Text>
          </ListItemCustom>
        );
      }
      if ('tagName' in domNode && domNode.tagName === 'sup') {
        return <SupElement>{domToReact(domNode.children as DOMNode[], textOptions)}</SupElement>;
      }

      return null;
    },
  };

  const inPackageItems = [
    ...(splittedPackageContent?.withImages ?? []),
    ...(splittedPackageContent?.rest ?? []),
  ];
  const inPackageItemsLength = inPackageItems.length;

  const isAtLeastMBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_MD) === 'desktop';
  const isAtLeastLBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_LG) === 'desktop';
  const isAtLeastXLBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_XL) === 'desktop';

  const itemsPerView = useMemo(() => {
    if (!isAtLeastMBreakpoint) {
      return 1;
    }
    return 2;
  }, [isAtLeastMBreakpoint, isAtLeastLBreakpoint, isAtLeastXLBreakpoint]);

  const [currentPage, setCurrentPage] = useState(0);
  const getPaginatedImages = (images: Features[], current: number) => {
    const startIndex = current * itemsPerView;
    const endIndex = startIndex + itemsPerView;
    return images.slice(startIndex, endIndex);
  };

  const getCurrentInPackage = useMemo(() => {
    return getPaginatedImages(inPackageItems, currentPage);
  }, [inPackageItems, currentPage, itemsPerView]);

  const inPackagesSliced = getCurrentInPackage.slice(0, itemsPerView);

  const numberOfPagesForInPackages = useMemo(() => {
    return Math.ceil(inPackageItemsLength / itemsPerView);
  }, [inPackageItemsLength, itemsPerView]);

  const handlePageChange = (
    eventName: 'prev' | 'next',
    setPage: React.Dispatch<React.SetStateAction<number>>,
    maxPage: number
  ) => {
    setPage((prev) => {
      if (eventName === 'prev') return Math.max(prev - 1, 0);
      if (eventName === 'next') return Math.min(prev + 1, maxPage - 1);
      return prev;
    });
  };

  const onPageChange = (eventName: 'prev' | 'next') => {
    handlePageChange(eventName, setCurrentPage, numberOfPagesForInPackages);
  };

  const tyreLabels = isTyre(product.prno) ? getTyreLabelLinks(vehicleRaw) : null;
  useEffect(() => {
    setCurrentPage(0);
    setCurrentPage(0);
  }, [itemsPerView]);
  return (
    <ModalContentContainer>
      <HeadlineAndInfoContainer>
        {headline?.text && (
          <HeadlinePanel>
            <Headline
              tag="h3"
              variant={{ type: 'headline', order: '3', weight: 'normal' }}
              data-testid="equipmentsHeadline"
            >
              <DangerousElement as="span" html={headline.text} />
            </Headline>
          </HeadlinePanel>
        )}
        <InfoPanel>
          {createProductHero(product.name, productImage, productVideo)}
          <InfoTextContainer>
            {intro?.text && (
              <Text variant={{ type: 'copy', order: '2', style: 'normal' }} data-testid="intro">
                <DangerousElement as="span" html={intro.text} />
              </Text>
            )}
            {description?.text && (
              <Text variant={{ type: 'copy', order: '2', style: 'normal' }} data-testid="desc">
                {parse(description.text, textOptions)}
              </Text>
            )}
            {benefits?.text && (
              <>
                {benefitsHeadline && (
                  <Text
                    variant={{ type: 'copy', order: '2', style: 'normal' }}
                    data-testid="benefitsHeadline"
                    margin={[SYS_SPACE_RELATIVE_BP_SM_LG, 0, 0, 0]}
                  >
                    <DangerousElement as="span" html={benefitsHeadline} />
                  </Text>
                )}
                <Text
                  tag="p"
                  variant={{ type: 'copy', order: '2', style: 'normal' }}
                  data-testid="benefitsText"
                >
                  {parse(benefits.text, textOptions)}
                </Text>
              </>
            )}
            {packageContent && packageContent.length > 0 && (
              <>
                {packageContentHeadline && (
                  <Headline
                    tag="h4"
                    variant={{ type: 'headline', order: '4', weight: 'normal' }}
                    data-testid="inPackageContents"
                    margin={[SYS_SPACE_RELATIVE_BP_SM_2_XL, 0, 0, 0]}
                  >
                    <DangerousElement as="span" html={packageContentHeadline} />
                  </Headline>
                )}
                <InPackageProductsContainer data-testid="inPackageItems">
                  {inPackagesSliced.map((feature) => (
                    <Product product={feature} key={feature.prno} vehicleRaw={vehicleRaw} />
                  ))}
                </InPackageProductsContainer>
                <EquipmentsPagination
                  totalPages={numberOfPagesForInPackages}
                  current={currentPage}
                  onPageChange={onPageChange}
                />
              </>
            )}
            {remarks?.text && (
              <Text
                variant={{ type: 'copy', order: '2', style: 'normal' }}
                data-testid="remarksText"
              >
                <DangerousElement as="span" html={remarks.text} />
              </Text>
            )}
            {tyreLabels && <TyreLabel tyreLabels={tyreLabels} />}
          </InfoTextContainer>
        </InfoPanel>
      </HeadlineAndInfoContainer>
    </ModalContentContainer>
  );
};

export default ModalContent;
